import { ResumeScoreCard } from '@/components/resume/resume-score-card'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useChatStreaming } from '@/hooks/use-chat-streaming'
import { useDocumentProcessingProgress } from '@/hooks/use-document-processing-progress'
import { useDocuments } from '@/hooks/use-documents'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { cn } from '@/lib/utils'
import { documentService } from '@/services/document.service'
import { useChatStore } from '@/stores/chat-store'
import { useUploadProgressStore } from '@/stores/upload-progress-store'
import type { Document, DocumentDetail } from '@/types'
import { FileText, Loader, Paperclip, ScrollText, Upload } from 'lucide-react'
import type { ChangeEvent, DragEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPTED_TYPES = ['application/pdf']

export function ResumePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { sendMessageStreaming } = useChatStreaming()
  const { chatId } = useChatStore()
  const [isEnteringFromWelcome, setIsEnteringFromWelcome] = useState(
    Boolean(location.state?.fromWelcome),
  )
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [documentDetails, setDocumentDetails] = useState<Record<number, DocumentDetail>>({})
  const loadingDetailIdsRef = useRef<Set<number>>(new Set())
  const uploadStore = useUploadProgressStore()
  const { data: documents, isLoading, refetch } = useDocuments()
  const { progressMap } = useDocumentProcessingProgress(documents)

  // Load document details for all resumes
  useEffect(() => {
    if (!documents) return
    const resumes = documents.filter((d) => d.document_type === 'resume' && d.status === 'ready')
    for (const doc of resumes) {
      if (loadingDetailIdsRef.current.has(doc.id)) continue

      loadingDetailIdsRef.current.add(doc.id)
      documentService
        .getDocument(doc.id)
        .then((detail) => {
          setDocumentDetails((prev) => {
            if (prev[doc.id]) return prev
            return { ...prev, [doc.id]: detail }
          })
        })
        .catch((err) => console.error('Failed to load document detail:', err))
        .finally(() => {
          loadingDetailIdsRef.current.delete(doc.id)
        })
    }
  }, [documents])

  const sortedResumes =
    documents
      ?.filter((d) => d.document_type === 'resume')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) || []
  const primaryReadyResumeId = sortedResumes.find((doc) => doc.status === 'ready')?.id ?? null

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      if (file.size > MAX_FILE_SIZE) {
        setUploadError('File must be under 5MB')
        return
      }
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setUploadError('Only PDF files are supported')
        return
      }

      setIsUploading(true)
      setUploadError(null)
      uploadStore.setIsUploading(true)
      uploadStore.setUploadProgress(5, 'Preparing upload...', file.name)

      try {
        const { document_id, upload_url } = await documentService.getUploadUrl({
          filename: file.name,
          content_type: file.type,
          file_size: file.size,
        })

        uploadStore.setUploadProgress(20, 'Uploading your resume...')
        await documentService.uploadToS3(upload_url, file)
        uploadStore.setUploadProgress(50, 'Reading your resume...')
        await documentService.startProcessing(document_id)
        uploadStore.setUploadProgress(60, 'Evaluating skills...')
        await refetch()
        // Document is now in the list; useDocumentProcessingProgress takes over
        uploadStore.reset()
      } catch (err) {
        console.error('[resume] Upload error:', err)
        setUploadError(
          err instanceof Error
            ? err.message
            : (err as { message?: string })?.message || 'Upload failed',
        )
        uploadStore.reset()
      } finally {
        setIsUploading(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    },
    [refetch, uploadStore],
  )

  const handleDelete = useCallback(
    async (doc: Document) => {
      const confirmed = window.confirm(
        `Delete "${doc.original_filename}"?\n\nThis will also delete all job searches linked to this resume.`,
      )
      if (!confirmed) return
      setDeletingId(doc.id)
      try {
        await documentService.deleteDocument(doc.id)
        await refetch()
      } catch (err) {
        console.error('Delete failed:', err)
      } finally {
        setDeletingId(null)
      }
    },
    [refetch],
  )

  const handleDownload = useCallback(async (doc: Document) => {
    try {
      const detail = await documentService.getDocument(doc.id)
      if (detail.revisions && detail.revisions.length > 0) {
        const readyRevisions = detail.revisions.filter((r) => r.status === 'ready')
        const latestReadyRevision = readyRevisions.sort(
          (a, b) => b.revision_number - a.revision_number,
        )[0]
        if (latestReadyRevision) {
          const { download_url } = await documentService.getRevisionDownloadUrl(
            doc.id,
            latestReadyRevision.id,
            'pdf',
          )
          window.open(download_url, '_blank')
          return
        }
      }
      const { download_url } = await documentService.getOriginalDownloadUrl(doc.id)
      window.open(download_url, '_blank')
    } catch (err) {
      console.error('Download failed:', err)
    }
  }, [])

  const [isDragging, setIsDragging] = useState(false)

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (!file) return
    // Reuse the same validation and upload logic via the file input
    const dt = new DataTransfer()
    dt.items.add(file)
    if (fileInputRef.current) {
      fileInputRef.current.files = dt.files
      fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }, [])

  // Fade-in animation when arriving from the welcome step.
  // Split into two effects so that clearing location state does not cancel the
  // requestAnimationFrame that removes the opacity-0 class.
  useEffect(() => {
    if (!isEnteringFromWelcome) return

    const rafId = requestAnimationFrame(() => {
      setIsEnteringFromWelcome(false)
    })

    return () => cancelAnimationFrame(rafId)
  }, [isEnteringFromWelcome])

  // Clear the fromWelcome location state so a browser back-navigation does not
  // replay the entrance animation.
  useEffect(() => {
    if (!location.state?.fromWelcome) return
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.pathname, location.state, navigate])

  return (
    <div
      className={cn(
        'h-full overflow-y-auto bg-[#F3F4F6] dark:bg-background transition-all duration-700 ease-out',
        isEnteringFromWelcome
          ? 'opacity-0 translate-y-2 scale-[0.985]'
          : 'opacity-100 translate-y-0 scale-100',
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3">
          <h1 className="text-xl md:text-2xl font-semibold flex items-center gap-2.5">
            <ScrollText className="h-6 w-6 text-foreground" />
            Resume
          </h1>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button
            onClick={handleUploadClick}
            disabled={isUploading}
            className="rounded-lg bg-blue-600 hover:bg-blue-700 shadow-md gap-2"
          >
            {isUploading ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Add Resume
              </>
            )}
          </Button>
        </div>

        {uploadError && (
          <Card className="mb-6 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
            <CardContent className="py-3 text-red-600 dark:text-red-400">{uploadError}</CardContent>
          </Card>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : sortedResumes.length > 0 || isUploading ? (
          <div className="space-y-4">
            {/* Show placeholder card while uploading before document appears in list */}
            {isUploading && !sortedResumes.some((d) => d.status === 'uploading') && (
              <Card className="border bg-white dark:bg-gray-900/50 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">
                        {uploadStore.uploadFilename?.replace(/\.[^.]+$/, '') || 'Resume'}
                      </h3>
                      <p className="text-sm text-muted-foreground">Uploading now</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm text-blue-600 bg-blue-50 dark:bg-blue-950/30 px-3 py-1 rounded-full">
                      <Loader className="h-3.5 w-3.5 animate-spin" />
                      {uploadStore.statusText || 'Uploading...'}
                    </span>
                  </div>
                  {uploadStore.uploadProgress > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">
                          {uploadStore.statusText || 'Uploading...'}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {Math.round(uploadStore.uploadProgress)}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all duration-500"
                          style={{ width: `${uploadStore.uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {sortedResumes.map((doc) => (
              <ResumeScoreCard
                key={doc.id}
                doc={doc}
                detail={documentDetails[doc.id] || null}
                onDelete={handleDelete}
                onDownload={handleDownload}
                isDeleting={deletingId === doc.id}
                processingProgress={progressMap[doc.id]?.progress ?? null}
                processingStatusText={progressMap[doc.id]?.statusText ?? null}
                isPrimary={doc.id === primaryReadyResumeId}
              />
            ))}
          </div>
        ) : (
          /* Empty State - Drop zone */
          <div className="min-h-[70vh] flex items-center justify-center px-4">
            <Card
              className={cn(
                'w-full max-w-[461px] rounded-3xl border bg-white shadow-sm transition-colors',
                isDragging ? 'border-blue-400 bg-blue-50/30' : 'border-slate-200',
              )}
            >
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="mb-4 rounded-full border border-slate-200 p-2">
                  <FileText
                    className={cn('h-5 w-5', isDragging ? 'text-blue-500' : 'text-slate-500')}
                  />
                </div>
                <h3 className="text-lg font-semibold tracking-tight mb-2">
                  {isDragging ? 'Drop your resume here' : 'Drop or upload your resume'}
                </h3>
                <p className="text-xs text-muted-foreground mb-5">
                  Supported format: PDF (max 5MB)
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleUploadClick}
                    disabled={isUploading}
                    size="default"
                    className="rounded-md bg-blue-600 hover:bg-blue-700 px-12"
                  >
                    Browse
                  </Button>
                  <button
                    type="button"
                    aria-label="Attach resume"
                    onClick={handleUploadClick}
                    className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-300 text-slate-500 hover:bg-slate-100 transition-colors"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-4 text-xs text-muted-foreground">
                  No resume? No worries!{' '}
                  <button
                    type="button"
                    className="text-blue-600 hover:underline"
                    onClick={() => {
                      if (!chatId) return
                      const prompt = "I don't have a resume. Can you help me draft one"
                      if (isMobile) {
                        sessionStorage.setItem(
                          'pending_coach_message',
                          JSON.stringify({ message: prompt }),
                        )
                        navigate('/chat')
                        return
                      }
                      sendMessageStreaming(prompt)
                    }}
                  >
                    Help me draft a resume
                  </button>
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
